import {
  QueryClient,
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useQuery as useReactQuery,
  useInfiniteQuery as useReactQueryInfiniteQuery,
  useMutation as useReactQueryMutation
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { ErrorResponse } from '@/types';

export type ServiceResponse<TData = unknown> = AxiosResponse<TData>;
export type ServiceError = ErrorResponse;

export type MutationOptions<
  TPayload = void,
  TResponseData = TPayload,
  TContext = unknown
> = Omit<
  UseMutationOptions<ServiceResponse<TResponseData>, ServiceError, TPayload, TContext>,
  'mutationFn'
> & {
  queryClient?: QueryClient;
  invalidateQueries?: QueryKey[];
};

export type QueryOptions<
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey
> = Omit<
  UseQueryOptions<
    ServiceResponse<TData>,
    ServiceError,
    ServiceResponse<TData>,
    TQueryKey
  >,
  'queryKey' | 'queryFn'
>;

export type InfiniteQueryOptions<
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey
> = Omit<
  UseInfiniteQueryOptions<
    ServiceResponse<TData>,
    ServiceError,
    ServiceResponse<TData>,
    ServiceResponse<TData>,
    TQueryKey
  >,
  'queryKey' | 'queryFn'
>;

export const useQuery = <
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey
>(
    queryKey: TQueryKey,
    queryFn: () => Promise<ServiceResponse<TData>>,
    options?: QueryOptions<TData, TQueryKey>
  ) => {
  return useReactQuery<ServiceResponse<TData>, ServiceError, ServiceResponse<TData>, TQueryKey>(
    queryKey,
    queryFn,
    options
  );
};

export const useInfiniteQuery = <
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey
>(
    queryKey: TQueryKey,
    queryFn: (pageParam: unknown) => Promise<ServiceResponse<TData>>,
    options?: InfiniteQueryOptions<TData, TQueryKey>
  ) => {
  return useReactQueryInfiniteQuery<
    ServiceResponse<TData>,
    ServiceError,
    ServiceResponse<TData>,
    TQueryKey
  >(
    queryKey,
    ({ pageParam }) => queryFn(pageParam),
    options
  );
};

export const useMutation = <
  TPayload = void,
  TResponseData = TPayload,
  TContext = unknown
>(
    mutationFn: (payload: TPayload) => Promise<ServiceResponse<TResponseData>>,
    options?: MutationOptions<TPayload, TResponseData, TContext>
  ) => {
  const { queryClient, invalidateQueries, ...mutationOptions } = options ?? {};

  return useReactQueryMutation<ServiceResponse<TResponseData>, ServiceError, TPayload, TContext>(
    mutationFn,
    {
      ...mutationOptions,
      onSuccess: (data, variables, context) => {
        mutationOptions.onSuccess?.(data, variables, context);
        invalidateQueries?.forEach((queryKey) => {
          queryClient?.invalidateQueries(queryKey);
        });
      },
      onError: (error, variables, context) => {
        mutationOptions.onError?.(error, variables, context);
      }
    }
  );
};
